/* Visible choices mirror the existing controls. Studios still own validation, prices and orders. */
(() => {
  'use strict';
  if (window.KaliaVisibleOptions) return;
  const selector = [
    '.kg-contact-form select', '[data-km-system]', '[data-swatch-address-country]',
    'kalia-product-studio select', 'kalia-drapery select', 'kalia-hardware select',
    '.klc select[name="quantity"]'
  ].join(',');
  const disclosureSelector = '.kg-help-more, .kps-finishing, .kps-room, .kalia-optional-details';
  const controls = new Map();
  let sequence = 0, queued = false;
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  const optionsFor = select => [...select.options].filter(option => !option.hidden && !option.parentElement?.hidden);
  const unavailable = option => option.disabled || (option.parentElement?.tagName === 'OPTGROUP' && option.parentElement.disabled);
  const titleFor = select => {
    if (select.getAttribute('aria-label')) return select.getAttribute('aria-label');
    const label = select.labels?.[0];
    return label ? [...label.childNodes].filter(child => child !== select && !child.classList?.contains('kvo')).map(child => child.textContent).join(' ').replace(/\s+/g, ' ').replace(/\s*\*\s*$/, '').trim() : 'Choose an option';
  };
  class VisibleChoice {
    constructor(select) {
      this.select = select;
      this.title = titleFor(select);
      this.quantity = select.name === 'quantity';
      this.group = node('div', this.quantity ? 'kvo kvo--quantity' : 'kvo');
      this.group.setAttribute('role', this.quantity ? 'group' : 'radiogroup');
      this.group.setAttribute('aria-label', this.title);
      if (select.getAttribute('aria-describedby')) this.group.setAttribute('aria-describedby', select.getAttribute('aria-describedby'));
      if (select.required) this.group.setAttribute('aria-required', 'true');
      if (/fraction/i.test(select.name || select.getAttribute('aria-label') || '')) this.group.classList.add('kvo--fraction');
      select.after(this.group);
      select.classList.add('kvo-native');
      select.setAttribute('aria-hidden', 'true');
      select.tabIndex = -1;
      select.dataset.visibleOptions = String(++sequence);
      this.error = node('p', 'kvo-error'); this.error.id = `KaliaVisibleOptionError-${sequence}`; this.error.hidden = true; this.error.setAttribute('role', 'alert');
      this.group.after(this.error);
      this.group.setAttribute('aria-describedby', [select.getAttribute('aria-describedby'), this.error.id].filter(Boolean).join(' '));
      select.addEventListener('input', queue);
      select.addEventListener('change', queue);
      select.addEventListener('focus', () => this.focus());
      select.addEventListener('invalid', event => { event.preventDefault(); this.group.setAttribute('aria-invalid', 'true'); this.error.textContent = 'Choose an option to continue.'; this.error.hidden = false; this.focus(); });
      this.sync();
    }
    choose(value) {
      const option = optionsFor(this.select).find(item => item.value === value);
      if (this.select.disabled || !option || unavailable(option)) return;
      this.select.value = value;
      this.group.removeAttribute('aria-invalid');
      this.select.dispatchEvent(new Event('input', {bubbles: true}));
      this.select.dispatchEvent(new Event('change', {bubbles: true}));
      this.sync();
    }
    focus() { (this.buttons?.find(button => button.getAttribute('aria-checked') === 'true' && !button.disabled) || this.buttons?.find(button => !button.disabled) || this.output)?.focus(); }
    build(options) {
      const focused = this.group.contains(document.activeElement);
      this.group.replaceChildren();
      if (this.quantity) {
        this.previous = node('button', 'kvo-step', '−'); this.previous.type = 'button'; this.previous.setAttribute('aria-label', 'Decrease quantity');
        this.output = node('output', 'kvo-count'); this.output.setAttribute('aria-live', 'polite'); this.output.setAttribute('aria-label', 'Quantity'); this.output.tabIndex = -1;
        this.next = node('button', 'kvo-step', '+'); this.next.type = 'button'; this.next.setAttribute('aria-label', 'Increase quantity');
        const move = direction => {
          const available = optionsFor(this.select).filter(option => !unavailable(option));
          const index = available.findIndex(option => option.value === this.select.value);
          const target = available[index + direction];
          if (target) this.choose(target.value);
        };
        this.previous.addEventListener('click', event => { event.preventDefault(); move(-1); });
        this.next.addEventListener('click', event => { event.preventDefault(); move(1); });
        this.group.append(this.previous, this.output, this.next);
      } else {
        const entries = options.filter(option => option.value !== '' || option === options[options.length - 1]);
        this.buttons = entries.map(option => {
          const button = node('button', 'kvo-option', option.textContent.trim());
          button.type = 'button'; button.dataset.value = option.value; button.setAttribute('role', 'radio');
          button.addEventListener('click', event => { event.preventDefault(); this.choose(option.value); });
          button.addEventListener('keydown', event => {
            if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            const enabled = this.buttons.filter(item => !item.disabled);
            const index = enabled.indexOf(button);
            const next = event.key === 'Home' ? enabled[0] : event.key === 'End' ? enabled[enabled.length - 1] : enabled[(index + (/Left|Up/.test(event.key) ? -1 : 1) + enabled.length) % enabled.length];
            if (next) { this.choose(next.dataset.value); next.focus(); }
          });
          this.group.append(button); return button;
        });
      }
      if (focused) this.focusAfterSync = true;
    }
    sync() {
      const options = optionsFor(this.select);
      const key = JSON.stringify(options.map(option => [option.value, option.textContent, unavailable(option)]));
      if (key !== this.key) { this.key = key; this.build(options); }
      this.group.hidden = this.select.hidden;
      if (this.select.validity.valid) { this.group.removeAttribute('aria-invalid'); this.error.hidden = true; }
      this.group.setAttribute('aria-disabled', String(this.select.disabled));
      if (this.quantity) {
        const available = options.filter(option => !unavailable(option));
        const index = available.findIndex(option => option.value === this.select.value);
        if (this.output.textContent !== this.select.value) this.output.textContent = this.select.value;
        this.previous.disabled = this.select.disabled || index <= 0;
        this.next.disabled = this.select.disabled || index < 0 || index >= available.length - 1;
      } else {
        const enabled = this.buttons.filter(button => {
          const option = options.find(item => item.value === button.dataset.value);
          button.disabled = this.select.disabled || !option || unavailable(option);
          const checked = this.select.value === button.dataset.value;
          button.setAttribute('aria-checked', String(checked));
          button.tabIndex = -1;
          return !button.disabled;
        });
        const active = enabled.find(button => button.dataset.value === this.select.value) || enabled[0];
        if (active) active.tabIndex = 0;
      }
      if (this.focusAfterSync) { this.focusAfterSync = false; this.focus(); }
    }
  }
  function scan() {
    document.querySelectorAll(selector).forEach(select => {
      // Address-region lists and large native variant fallbacks retain their own search/selection UI.
      if (!controls.has(select) && !select.multiple && !select.hidden && !select.closest('[data-fallback-variant]') && select.options.length > 0 && select.options.length <= 24) controls.set(select, new VisibleChoice(select));
    });
    controls.forEach((control, select) => {
      if (!select.isConnected) { control.group.remove(); control.error.remove(); controls.delete(select); }
      else control.sync();
    });
    document.querySelectorAll(disclosureSelector).forEach(details => {
      if (!details.open) details.open = true;
      if (details.dataset.visibleConfiguration === 'true') return;
      details.dataset.visibleConfiguration = 'true';
      const summary = details.querySelector(':scope > summary');
      if (summary) {
        if (details.matches('.kps-room')) { const optional = summary.querySelector('span'); if (optional) optional.textContent = 'Optional'; }
        summary.tabIndex = -1; summary.setAttribute('role', 'heading'); summary.setAttribute('aria-level', '3');
        summary.addEventListener('click', event => event.preventDefault());
      }
    });
  }
  function queue() {
    if (queued) return;
    queued = true;
    queueMicrotask(() => { queued = false; scan(); });
  }
  window.KaliaVisibleOptions = {refresh: queue, focus: select => controls.get(select)?.focus()};
  const initialize = () => {
    scan();
    const observer = new MutationObserver(records => {
      if (records.some(record => record.type === 'childList' ? !record.target.closest?.('.kvo') : record.target.matches?.('select, option, optgroup, details'))) queue();
    });
    observer.observe(document.body, {subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'hidden', 'selected', 'open']});
    for (const event of ['click', 'input', 'change', 'reset', 'shopify:section:load', 'kalia:product-configuration-change']) document.addEventListener(event, queue);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, {once: true}); else initialize();
})();
